import { useCallback, useEffect, useRef, useState } from "react";
import { TextField, Text, Badge, BlockStack, InlineStack, Box } from "@shopify/polaris";
import supabase from "../../supabase-client";

// Finding out whose money this is.
//
// A SPEI to a per-payer CLABE says who sent it; a card does not. Most card
// charges therefore arrive "Sin identificar", and this is how a person resolves
// one — by the three things they actually have in front of them: the client's
// name, the folio of the invoice being paid, or a reservation number.
//
// Searching a reservation or a folio does double duty: it identifies the client
// AND remembers which reservation was asked about, so the modal can open on
// that one already ticked. Looking up "1440" and then hunting for reservation
// 1440 in a list of eleven would be doing the same work twice.
//
// Nothing here writes anything. Picking a client only tells the modal who to
// show; the payment is not touched until Aplicar.

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

// PostgREST filters are parsed as a little language, so commas, parentheses and
// wildcards in a search box would change the query rather than be searched for.
const safeLike = (s) => s.replace(/[,()%*\\]/g, " ").trim();

export default function PayerPicker({ onPick, autoFocus = true }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  // Only the newest search may write results: a slow request for "14" must not
  // land after the one for "1440" and overwrite it.
  const seq = useRef(0);

  const search = useCallback(async (raw) => {
    const q = raw.trim();
    const mine = ++seq.current;
    if (q.length < MIN_CHARS) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    const like = safeLike(q);
    const asId = /^\d+$/.test(q) ? Number(q) : null;

    const [clients, reservation, invoices] = await Promise.all([
      supabase.rpc("search_clients", { search_text: q }),
      asId
        ? supabase
            .from("reservations")
            .select("id, status, customer, company, customers(first_name, last_name), companies(name)")
            .eq("id", asId)
            .maybeSingle()
        : Promise.resolve({ data: null }),
      like
        ? supabase
            .from("invoices_issued")
            .select("id, invoice_number, total, reservation_id, customer_id, company_id, customers(first_name, last_name), companies(name)")
            .ilike("invoice_number", `%${like}%`)
            .order("date", { ascending: false })
            .limit(5)
        : Promise.resolve({ data: [] }),
    ]);

    if (mine !== seq.current) return;               // a newer search won

    const out = [];
    const seen = new Set();
    // A client can legitimately turn up by name, by folio and by reservation at
    // once. The first hit wins, and the ones carrying a reservation come first
    // because they save the most clicks.
    const push = (row) => {
      const key = `${row.payer_type}:${row.payer_id}`;
      if (!row.payer_id || seen.has(key)) return;
      seen.add(key);
      out.push(row);
    };

    const r = reservation?.data;
    if (r) {
      const payerType = r.company ? "company" : "customer";
      const payerId = r.company || r.customer;
      push({
        payer_type: payerType,
        payer_id: payerId,
        name: nameOf(r, payerType),
        detail: `Reservación #${r.id}`,
        focusReservationId: r.id,
        via: "reservación",
      });
    }

    for (const inv of invoices?.data || []) {
      const payerType = inv.company_id ? "company" : "customer";
      push({
        payer_type: payerType,
        payer_id: inv.company_id || inv.customer_id,
        name: nameOf(inv, payerType),
        detail: `Folio ${inv.invoice_number}${inv.reservation_id ? ` · reservación #${inv.reservation_id}` : ""}`,
        focusReservationId: inv.reservation_id || null,
        via: "folio",
      });
    }

    for (const c of clients?.data || []) {
      push({
        payer_type: c.client_type === "company" ? "company" : "customer",
        payer_id: c.id,
        name: c.name,
        detail: c.detail,
        focusReservationId: null,
        via: "cliente",
      });
    }

    setResults(out.slice(0, 8));
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, search]);

  return (
    <BlockStack gap="300">
      <TextField
        label="¿De quién es este pago?"
        value={query}
        onChange={setQuery}
        placeholder="Nombre del cliente, folio de factura o número de reservación"
        autoComplete="off"
        loading={loading}
        focused={autoFocus}
        clearButton
        onClearButtonClick={() => setQuery("")}
      />

      {results !== null && results.length === 0 && !loading && (
        <Text tone="subdued" variant="bodySm">Sin resultados para “{query.trim()}”.</Text>
      )}

      {results?.map((r) => (
        <Box
          key={`${r.payer_type}:${r.payer_id}`}
          borderWidth="025"
          borderColor="border"
          borderRadius="200"
          padding="300"
        >
          <div
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => onPick(r)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPick(r)}
          >
            <InlineStack align="space-between" blockAlign="center" gap="300" wrap={false}>
              <BlockStack gap="050">
                <Text as="span" fontWeight="semibold">{r.name}</Text>
                {r.detail && <Text as="span" tone="subdued" variant="bodySm">{r.detail}</Text>}
              </BlockStack>
              <Badge tone={r.via === "cliente" ? undefined : "info"}>{r.via}</Badge>
            </InlineStack>
          </div>
        </Box>
      ))}
    </BlockStack>
  );
}

function nameOf(row, payerType) {
  if (payerType === "company") return row.companies?.name || "Empresa";
  const c = row.customers;
  return `${c?.first_name || ""} ${c?.last_name || ""}`.replace(/\s+/g, " ").trim() || "Cliente";
}
